import type { ApiReferenceConfiguration } from '@scalar/types/api-reference'
import { resolve } from '@scalar/workspace-store/resolve'
import type { DiscriminatorObject, SchemaObject } from '@scalar/workspace-store/schemas/v3.1/strict/openapi-document'

import { isTypeObject } from './is-type-object'

/** Extract the type of properties */
type Properties = NonNullable<Extract<SchemaObject, { type: 'object' }>['properties']>

type Options = Partial<
  Pick<ApiReferenceConfiguration, 'orderSchemaPropertiesBy' | 'orderRequiredPropertiesFirst'> & {
    hideReadOnly: boolean
    hideWriteOnly: boolean
  }
>

/** Take a list of property names and reduce it back into an object */
export const reduceNamesToObject = (names: string[], properties: Properties): Properties =>
  names.reduce((acc, name) => {
    const prop = properties?.[name]
    if (prop) {
      acc[name] = prop
    }
    return acc
  }, {} as Properties)

/** Sort property names in an object schema */
export const sortPropertyNames = (
  schema: SchemaObject,
  discriminator?: DiscriminatorObject,
  {
    hideReadOnly = false,
    hideWriteOnly = false,
    orderSchemaPropertiesBy = 'alpha',
    orderRequiredPropertiesFirst = true,
  }: Options = {},
): string[] => {
  if (!isTypeObject(schema) || !schema.properties) {
    return []
  }

  const propertyNames = Object.keys(schema.properties)
  const requiredPropertiesSet = new Set(schema.required || [])

  return propertyNames
    .sort((a, b) => {
      const aDiscriminator = a === discriminator?.propertyName
      const bDiscriminator = b === discriminator?.propertyName

      const aRequired = requiredPropertiesSet.has(a)
      const bRequired = requiredPropertiesSet.has(b)

      // Discriminator comes first always
      if (aDiscriminator && !bDiscriminator) {
        return -1
      }
      if (!aDiscriminator && bDiscriminator) {
        return 1
      }

      // Sort by x-order specification extension when present
      const aSchema = schema.properties?.[a]
      const bSchema = schema.properties?.[b]
      const aOrder =
        aSchema && typeof aSchema === 'object' && 'x-order' in aSchema
          ? (aSchema as Record<string, unknown>)['x-order']
          : undefined
      const bOrder =
        bSchema && typeof bSchema === 'object' && 'x-order' in bSchema
          ? (bSchema as Record<string, unknown>)['x-order']
          : undefined

      if (aOrder !== undefined && bOrder !== undefined) {
        return Number(aOrder) - Number(bOrder)
      }
      if (aOrder !== undefined && bOrder === undefined) {
        return -1
      }
      if (aOrder === undefined && bOrder !== undefined) {
        return 1
      }

      // Order required properties first
      if (orderRequiredPropertiesFirst) {
        // If one is required and the other isn't, required comes first
        if (aRequired && !bRequired) {
          return -1
        }
        if (!aRequired && bRequired) {
          return 1
        }
      }

      // If both have the same required status, sort alphabetically
      if (orderSchemaPropertiesBy === 'alpha') {
        return a.localeCompare(b)
      }

      return 0
    })
    .filter((property) => {
      const resolved = schema.properties && resolve.schema(schema.properties[property])

      // If hideReadOnly is true, filter out properties that are readOnly
      if (hideReadOnly && resolved?.readOnly === true) {
        return false
      }
      // If hideWriteOnly is true, filter out properties that are writeOnly
      if (hideWriteOnly && resolved?.writeOnly === true) {
        return false
      }
      return true
    })
}
